// Demo-only types for the new contract/fulfillment model. These mirror
// what the real backend rows will look like once we ship; the demo
// keeps everything in React state via ContractsV2Context.

export type ProjectType = 'small' | 'large';
export type InitiatorRole = 'client' | 'creator';
export type AccountType = 'individual' | 'company';

export type ContractAcceptanceState =
  | 'draft'                  // initiator still editing
  | 'pending_other_side'     // sent, awaiting other side's review
  | 'rejected'
  | 'active'
  | 'completed'
  | 'cancelled'
  | 'disputed';

export type MilestoneStatus =
  | 'NOT_STARTED'
  | 'IN_PROGRESS'
  | 'SUBMITTED'              // creator submitted a delivery, validity check pending
  | 'IN_ACCEPTANCE'          // valid delivery, client in 验收期
  | 'REVISION_REQUESTED'
  | 'COMPLETED'
  | 'CANCELLED';

export type MilestonePaymentStatus = 'UNFUNDED' | 'PARTIALLY_FUNDED' | 'FUNDED' | 'RELEASED';

// Trigger is now derived from coverage:
//   first payment (covers M1+) → trigger is CONTRACT_SIGNED
//   payment that covers Mi+ where i>1 → trigger is "after M(i-1) 验收"
//   payment that covers only the tail → trigger is FINAL_TAIL
// We still expose this as an enum for display purposes only.
export type PaymentEntryTrigger =
  | 'CONTRACT_SIGNED'
  | 'MILESTONE_PASSED'       // tied to a specific milestoneId (the one *before* coverage)
  | 'FINAL_TAIL';

export type PaymentEntryStatus =
  | 'PENDING'                // not yet due
  | 'DUE'                    // trigger fired
  | 'PAID_BY_CLIENT'         // client says they paid; not yet platform-confirmed
  | 'PLATFORM_CONFIRMED'     // platform escrow holds it (post-连连 will be the real signal)
  | 'OVERDUE';

export type ReleaseEntryStatus = 'PENDING' | 'BLOCKED_NO_FUNDING' | 'RELEASED';

export interface DeliveryAsset {
  type: 'source_file' | 'model_file' | 'engineering_file' | 'commercial_license' | 'no_watermark' | 'prompt' | 'other';
  label: string;
  uploadedAt?: number;
  fileUrl?: string;
}

export interface Milestone {
  id: string;
  contractId: string;
  sequenceIndex: number;
  title: string;
  description: string;
  /**
   * Percent of contract.totalAmount that this milestone represents.
   * Authored as percent in the wizard; absolute amount is read-only
   * computed (= contract.totalAmount * percent / 100). Tail percent
   * is stored separately on the contract — milestone percents +
   * tailPercent must sum to 100.
   */
  percent: number;
  amount: number;        // computed mirror of percent for read sites
  dueAt: number;
  acceptanceCriteria: string;
  revisionLimit: number;
  revisionCountUsed: number;
  requiredAssets: DeliveryAsset[]; // configured at creation
  submittedAssets: DeliveryAsset[]; // populated when creator submits
  lastDeliveryAt?: number;
  // Platform doesn't auto-judge "有效交付". The standard is shown to
  // both parties; the buyer decides during 验收期. The legacy
  // validityCheckPassed flag is kept for back-compat but new flows
  // shouldn't rely on it.
  validityCheckPassed?: boolean;
  validityIssues?: string[];
  status: MilestoneStatus;
  paymentStatus: MilestonePaymentStatus;
  acceptancePeriodEndsAt?: number; // computed when status flips to IN_ACCEPTANCE
  revisionDueAt?: number;          // current returnDeadline (latest request only — earlier ones are kept on revisionHistory)
  revisionHistory: RevisionRequest[]; // every revision the buyer has filed against this milestone, oldest first
}

// Buyer must classify why they're requesting a revision. The category
// controls the side effects:
//   detail            → 细节优化：counts toward revisionLimit, extends 截止日 to returnDueAt
//   invalid_delivery  → 无效交付：does NOT count toward revisionLimit, does NOT extend 截止日
//                       (creator is responsible for any resulting overdue penalty)
//   change            → 需求变动 / 新增需求：handled by the variation flow (ChangeModal),
//                       not by revision; this enum value is reserved for display only.
export type RevisionCategory = 'detail' | 'invalid_delivery' | 'change';

export interface RevisionRequest {
  id: string;
  requestedAt: number;
  reason: string;
  returnDueAt: number;
  category?: RevisionCategory;
  // Filled in once the creator submits a new delivery in response.
  resolvedAt?: number;
}

export interface PaymentPlanEntry {
  id: string;
  contractId: string;
  sequenceIndex: number;
  /**
   * Amount client owes for this checkpoint. Read-only computed —
   * derived by summing the percents of every milestone listed in
   * coversMilestoneIds, plus the tail when coversTail is true.
   */
  amount: number;
  /** User picks which milestones this payment covers (must be a contiguous block). */
  coversMilestoneIds: string[];
  /** Tail can only be covered by exactly one payment. */
  coversTail: boolean;
  /** Derived from coverage; persisted for display. */
  trigger: PaymentEntryTrigger;
  triggerMilestoneId?: string;
  status: PaymentEntryStatus;
  paidAt?: number;
  platformConfirmedAt?: number;
  isFinalTail: boolean;
}

export interface ReleasePlanEntry {
  id: string;
  contractId: string;
  sequenceIndex: number;
  milestoneId: string;
  amount: number;
  status: ReleaseEntryStatus;
  releasedAt?: number;
  // Computed at release time when last delivery is past dueAt
  lateFeeApplied?: number;
  daysLate?: number;
}

export interface ContractChange {
  id: string;
  contractId: string;
  proposedBy: 'client' | 'creator';
  proposedAt: number;
  scopeDelta: string;
  feeDelta: number;       // positive: client pays more, folds into next payment entry
  additionalDays: number;
  status: 'PENDING_AGREE' | 'AGREED' | 'REJECTED';
  resolvedAt?: number;
  foldsIntoPaymentEntryId?: string;
}

export interface ContractDispute {
  id: string;
  contractId: string;
  raisedBy: 'client' | 'creator';
  raisedAt: number;
  reason: string;
  status: 'OPEN' | 'PLATFORM_REVIEWING' | 'RESOLVED' | 'WITHDRAWN';
  resolution?: string;
  resolvedAt?: number;
}

export interface PartyInfo {
  userId: string;
  nickname: string;       // looked up from userId, displayed grey/non-editable
  accountType: AccountType;
}

export type SignStatus = 'pending' | 'signed';

export interface Contract {
  id: string;
  initiatorRole: InitiatorRole;
  client: PartyInfo;
  creator: PartyInfo;
  title: string;
  description: string;
  projectType: ProjectType;
  totalAmount: number;
  // Tail (尾款) — only relevant for 大项目. Persisted as percentage + computed amount.
  tailPercent: number;       // 0–30
  tailAmount: number;        // = totalAmount * tailPercent / 100
  // Denormalized fund-coverage state. Updated whenever a payment posts /
  // a release fires / a refund is issued.
  totalPaid: number;         // sum of payment entries with status >= PLATFORM_CONFIRMED, EXCLUDING tail until tail itself posts
  totalReleased: number;
  totalRefunded: number;
  acceptanceState: ContractAcceptanceState;
  createdAt: number;
  signedAt?: number;
  // Snapshot of buyer's accountType at sign time; used for 验收期 calc.
  accountTypeAtSign?: AccountType;
  // Default revision limit copied to each milestone at creation
  revisionLimitDefault: number;
  /**
   * Whether the buyer requires invoices for this contract's payments.
   * Set once at creation (typically by the buyer-side initiator for
   * enterprise accounts). Applies to every payment node.
   */
  requiresInvoice: boolean;
  // e签宝 state — populated when the receiving party clicks accept and
  // the signing flow is initiated. acceptanceState only flips to
  // 'active' once both signers complete (handled by the V2 webhook).
  esignFlowId?: string;
  esignDocId?: string;
  esignClientAccountId?: string;
  esignCreatorAccountId?: string;
  clientSignStatus?: SignStatus;
  creatorSignStatus?: SignStatus;
  clientSignedAt?: number;
  creatorSignedAt?: number;
  rejectionReason?: string;
  // userIds granted collaborator access via a share link.
  collaborators?: string[];
}

// Mutual post-completion review, one per side.
export interface ContractReview {
  contractId: string;
  reviewerUserId: string;
  revieweeUserId: string;
  reviewerRole: 'client' | 'creator';
  rating: number;        // 1–5
  comment: string;
  dimensionRatings?: Record<string, number>;
  createdAt: number;
}

// Invoice (发票) the creator uploads for the client post-completion.
export interface ContractInvoice {
  id: string;
  contractId: string;
  uploadedBy: string;
  fileUrl: string;
  fileName: string;
  amount?: number;
  note?: string;
  createdAt: number;
}

export interface ContractBundle {
  contract: Contract;
  milestones: Milestone[];
  paymentPlan: PaymentPlanEntry[];
  releasePlan: ReleasePlanEntry[];
  changes: ContractChange[];
  disputes: ContractDispute[];
  reviews: ContractReview[];
  invoices: ContractInvoice[];
}

// =====================================================================
// Computed views — never persisted, always derived. The UI calls these
// to render fund-coverage banners, milestone status pills, etc.
// =====================================================================

export interface FundCoverageView {
  totalPaid: number;
  totalReleased: number;
  totalRefunded: number;
  availableBalance: number;     // totalPaid - totalReleased - totalRefunded
  tailAmount: number;
  // For each milestone in sequence, can the project fund it without further client payment?
  perMilestone: Array<{
    milestoneId: string;
    cumulativeReleaseRequired: number;
    isCovered: boolean;
  }>;
}

// Acceptance period defaults from the spec (in days).
export const ACCEPTANCE_PERIOD_DEFAULTS = {
  small: 5,
  large: 7,
};
export const ACCEPTANCE_PERIOD_COMPANY_BONUS = 2;
// Subscription bonus is +2 but no subscription system yet, so unused.

// Project-type threshold (locked in by user spec)
export const SMALL_PROJECT_THRESHOLD = 1000;

// Tail rules — fixed three options now (per latest spec).
export const TAIL_PERCENT_OPTIONS = [10, 15, 20] as const;
export type TailPercent = typeof TAIL_PERCENT_OPTIONS[number];
// Minimum number of payment checkpoints. Two means "before & after",
// matching the agreed minimum (e.g. 签约 + 尾款).
export const MIN_PAYMENT_CHECKPOINTS = 2;

// Wire-format request the wizard sends to POST /api/v1/contracts-v2.
// Mirrors the backend's CreateContractRequest.
export interface CreateContractRequest {
  initiatorRole: InitiatorRole;
  client: PartyInfo;
  creator: PartyInfo;
  title: string;
  description: string;
  projectType: ProjectType;
  totalAmount: number;
  tailPercent: number;
  requiresInvoice: boolean;
  customPdfOssKey?: string;
  milestones: Array<Pick<Milestone,
    'title' | 'description' | 'percent' | 'dueAt' | 'acceptanceCriteria' | 'requiredAssets'
  >>;
  paymentPlan: Array<Pick<PaymentPlanEntry,
    'sequenceIndex' | 'coversMilestoneIds' | 'coversTail' | 'trigger' | 'triggerMilestoneId' | 'isFinalTail' | 'amount'
  >>;
}
